import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { ProgressCardItem as ProgressCardItemType } from './types';
import { ProgressDetailsMarkupMAIQ } from '../../ProgressDetailsMarkupMAIQ';
import { EmailTemplatePreviewDialog } from '../../EmailTemplatePreviewDialog';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../../redux/headerSlice/selectors';

const ItemContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 400,
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: '8px',
  overflow: 'hidden',
  maxWidth: '100%',
  height: 'auto',
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '300px',
  objectFit: 'contain',
  borderRadius: '8px',
}));

const PreviewButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.875rem',
  padding: '4px 12px',
  minWidth: 'auto',
  height: 'auto',
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    marginRight: theme.spacing(0.5),
  },
}));


interface DefaultProgressCardItemProps {
  item: ProgressCardItemType;
}

export const DefaultProgressCardItem: React.FC<DefaultProgressCardItemProps> = ({ item }) => {
  const { header, details, img_url, entity_type, entity_id } = item;
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Get selected dealer
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';
  
  // Check if this is an EMAIL_TEMPLATE entity (case-insensitive)
  const entityIdStr = entity_id ? String(entity_id).trim() : '';
  const isEmailTemplate = entity_type && entity_type.toUpperCase() === 'EMAIL_TEMPLATE' && entityIdStr !== '';
  
  // Debug logging
  React.useEffect(() => {
    if (entity_type || entity_id) {
      console.log('ProgressCardItem - entity_type:', entity_type, 'entity_id:', entity_id, 'entityIdStr:', entityIdStr, 'isEmailTemplate:', isEmailTemplate, 'item:', item);
    }
  }, [entity_type, entity_id, entityIdStr, isEmailTemplate, item]);
  
  // Handle preview button click
  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEmailTemplate && currentDealerId) {
      setPreviewOpen(true);
    } else {
      console.warn('Preview not available:', { isEmailTemplate, currentDealerId, entity_type, entity_id });
    }
  };
  
  // Close preview modal
  const handleClosePreview = () => {
    setPreviewOpen(false);
  };
  
  // Determine if this is a number value (for audience count)
  const isNumberValue = header?.toLowerCase().includes('count') || 
                       header?.toLowerCase().includes('number') ||
                       /^\d+$/.test(details || '');
  
  // Determine if this is a list (for filters)
  const isListValue = Array.isArray(details) || 
                     (typeof details === 'string' && details.includes('\n')) ||
                     header?.toLowerCase().includes('filter');

  const renderDetails = () => {
    if (!details) return null;

    if (isNumberValue) {
      return (
        <ProgressDetailsMarkupMAIQ 
          content={details || '0'} 
          variant="large" 
        />
      );
    }
    
    if (isListValue && typeof details === 'string') {
      return (
        <ProgressDetailsMarkupMAIQ 
          content={details} 
          variant="default" 
        />
      );
    }
    
    return (
      <ProgressDetailsMarkupMAIQ 
        content={details || ''} 
        variant="default" 
      />
    );
  };

  const renderImage = () => {
    if (!img_url) return null;

    return (
      <ImageContainer>
        <StyledImage
          src={img_url}
          alt={header || 'Progress item image'}
          onError={(e) => {
            console.error('Failed to load image:', img_url);
            // Hide image if it fails to load
            e.currentTarget.style.display = 'none';
          }}
        />
      </ImageContainer>
    );
  };

  if (!header && !details && !img_url && !isEmailTemplate) {
    return null;
  }

  return (
    <>
      <ItemContainer>
        {header && (
          <HeaderText>
            {header}
          </HeaderText>
        )}
        {renderDetails()}
        {renderImage()}
        {isEmailTemplate && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
            <PreviewButton
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={handlePreviewClick}
            >
              Preview
            </PreviewButton>
          </Box>
        )}
      </ItemContainer>
      
      {/* Preview Dialog */}
      {isEmailTemplate && (
        <EmailTemplatePreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          templateId={entityIdStr}
          dealerId={currentDealerId}
        />
      )}
    </>
  );
};